import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { LanguageProvider, detectPreferredLang } from './contexts/LanguageContext';
import { ROUTES, LANGUAGES, DEFAULT_LANG, localizedPath } from './seo/routes';
import { FormProvider } from './contexts/FormContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import type { Capability } from './config/capabilities';
import './i18n';
import './App.css';

// Public pages
import HomePage from './pages/HomePage';
import IntakePage from './pages/IntakePage';
import ManageAppointmentPage from './pages/ManageAppointmentPage';
import RecallUnsubscribePage from './pages/RecallUnsubscribePage';
import EmergencyPage from './pages/EmergencyPage';
import EmergencyBrokenToothPage from './pages/EmergencyBrokenToothPage';
import EmergencyToothachePage from './pages/EmergencyToothachePage';
import EmergencyTouristsPage from './pages/EmergencyTouristsPage';
import EmergencyAbscessPage from './pages/EmergencyAbscessPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalNoticePage from './pages/LegalNoticePage';
import ImplantsPage from './pages/ImplantsPage';
import VeneersPage from './pages/VeneersPage';
import CompositePage from './pages/CompositePage';
import TechnologiesPage from './pages/TechnologiesPage';
import EmergencyButton from './components/EmergencyButton';
import LanguageTest from './components/LanguageTest';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';

// Admin components
import LoginPage from './components/admin/LoginPage';
import MagicLinkVerifyPage from './components/admin/MagicLinkVerifyPage';
import AdminLayout from './components/admin/AdminLayout';
import Dashboard from './components/admin/Dashboard';
import CalendarView from './components/admin/CalendarView';
import ProvidersPage from './components/admin/ProvidersPage';
import ProceduresPage from './components/admin/ProceduresPage';
import TemplatesAdminPage from './components/admin/TemplatesAdminPage';
import AppointmentsPage from './components/admin/AppointmentsPage';
import QuickAppointmentPage from './components/admin/QuickAppointmentPage';
import IntakeListPage from './components/admin/IntakeListPage';
import PatientsListPage from './components/admin/PatientsListPage';
import PatientDetailPage from './components/admin/PatientDetailPage';
import InvoiceListPage from './components/admin/billing/InvoiceListPage';
import InvoiceEditorPage from './components/admin/billing/InvoiceEditorPage';
import CatalogPage from './components/admin/billing/CatalogPage';
import OperatoriesPage from './components/admin/OperatoriesPage';
import HelpPage from './components/admin/HelpPage';
import SettingsPage from './components/admin/SettingsPage';
import EventLogPage from './components/admin/EventLogPage';
import ReportsPage from './components/admin/ReportsPage';
import RemindersPage from './components/admin/RemindersPage';
import RecallsPage from './components/admin/RecallsPage';

// Protected route component for admin
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-navy-admin"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
};

// Bereichswächter: blockt den direkten Aufruf eines Bereichs ohne Berechtigung
// (Defense-in-Depth zusätzlich zum ausgeblendeten Menüpunkt). Fehlt das Recht,
// geht es zurück aufs Dashboard, das jeder Benutzer sehen darf.
const RequirePermission: React.FC<{ capability: Capability; children: React.ReactNode }> = ({ capability, children }) => {
  const { can } = useAuth();
  return can(capability) ? <>{children}</> : <Navigate to="/admin" replace />;
};

// Nur-Admin-Wächter für sensible Konfigurationsbereiche (Einstellungen, Stammdaten).
const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return user?.role === 'admin' ? <>{children}</> : <Navigate to="/admin" replace />;
};

// Map von Routen-Schlüssel (siehe src/seo/routes.json) auf die Seitenkomponente.
const PUBLIC_COMPONENTS: Record<string, React.FC> = {
  home: HomePage,
  implants: ImplantsPage,
  composite: CompositePage,
  veneers: VeneersPage,
  technologies: TechnologiesPage,
  emergency: EmergencyPage,
  'emergency-broken-tooth': EmergencyBrokenToothPage,
  'emergency-toothache': EmergencyToothachePage,
  'emergency-tourists': EmergencyTouristsPage,
  'emergency-abscess': EmergencyAbscessPage,
  contact: ContactPage,
  privacy: PrivacyPage,
  legal: LegalNoticePage,
};

// Redirect, der Query-String und Hash des aktuellen Pfads erhält (für Legacy-URLs).
const Redirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

// Einstieg ohne Sprach-Präfix ("/") → Weiterleitung auf die erkannte Sprache.
const RootRedirect: React.FC = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`${localizedPath('home', detectPreferredLang())}${search}${hash}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Patient intake questionnaire — token-based public access, own focused
              layout (no Header/Footer, own translations). Reached via email link. */}
          <Route path="/intake/:token" element={<IntakePage />} />
          <Route path="/termin/:token" element={<ManageAppointmentPage />} />
          <Route path="/recall/abmelden/:token" element={<RecallUnsubscribePage />} />

          {/* Admin Routes - separate layout (outside LanguageProvider) */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/login/verify" element={<MagicLinkVerifyPage />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<RequirePermission capability="calendar"><CalendarView /></RequirePermission>} />
            <Route path="quick-appointment" element={<RequirePermission capability="appointments"><QuickAppointmentPage /></RequirePermission>} />
            <Route path="appointments" element={<RequirePermission capability="appointments"><AppointmentsPage /></RequirePermission>} />
            <Route path="reminders" element={<RequirePermission capability="reminders"><RemindersPage /></RequirePermission>} />
            <Route path="recalls" element={<RequirePermission capability="recalls"><RecallsPage /></RequirePermission>} />
            <Route path="intake" element={<RequirePermission capability="intake"><IntakeListPage /></RequirePermission>} />
            <Route path="patients" element={<RequirePermission capability="patients"><PatientsListPage /></RequirePermission>} />
            <Route path="patients/:id" element={<RequirePermission capability="patients"><PatientDetailPage /></RequirePermission>} />
            <Route path="invoices" element={<RequirePermission capability="invoices"><InvoiceListPage /></RequirePermission>} />
            <Route path="invoices/catalog" element={<RequirePermission capability="invoices-manage"><CatalogPage /></RequirePermission>} />
            <Route path="invoices/new" element={<RequirePermission capability="invoices-manage"><InvoiceEditorPage /></RequirePermission>} />
            <Route path="invoices/:id" element={<RequirePermission capability="invoices"><InvoiceEditorPage /></RequirePermission>} />
            <Route path="providers" element={<AdminOnlyRoute><ProvidersPage /></AdminOnlyRoute>} />
            <Route path="procedures" element={<AdminOnlyRoute><ProceduresPage /></AdminOnlyRoute>} />
            <Route path="note-templates" element={<AdminOnlyRoute><TemplatesAdminPage /></AdminOnlyRoute>} />
            <Route path="operatories" element={<AdminOnlyRoute><OperatoriesPage /></AdminOnlyRoute>} />
            <Route path="reports" element={<RequirePermission capability="reports"><ReportsPage /></RequirePermission>} />
            <Route path="event-log" element={<RequirePermission capability="event-log"><EventLogPage /></RequirePermission>} />
            <Route path="settings" element={<AdminOnlyRoute><SettingsPage /></AdminOnlyRoute>} />
            <Route path="help" element={<HelpPage />} />
          </Route>

          {/* Public Website Routes - original layout (with LanguageProvider) */}
          <Route path="*" element={
            <LanguageProvider>
              <FormProvider>
                <div className="flex flex-col min-h-screen">
                  <Header />
                  <div className="flex-grow">
                    <Routes>
                      {/* Einstieg ohne Sprach-Präfix → erkannte Sprache */}
                      <Route path="/" element={<RootRedirect />} />

                      {/* Lokalisierte Routen: /<lang>/<slug> pro Seite × Sprache */}
                      {ROUTES.flatMap((route) =>
                        LANGUAGES.map((lang) => {
                          const Component = PUBLIC_COMPONENTS[route.key];
                          const slug = route.slugs[lang];
                          const path = slug ? `/${lang}/${slug}` : `/${lang}`;
                          return <Route key={`${route.key}-${lang}`} path={path} element={<Component />} />;
                        })
                      )}

                      <Route path="/test" element={<LanguageTest />} />

                      {/* Legacy-URLs (vor der Sprach-Umstellung) → präfixierte Version.
                          Deutsche Slugs → /de, ehemalige englische Aliase → /en. */}
                      <Route path="/implantate" element={<Redirect to="/de/implantate" />} />
                      <Route path="/veneers" element={<Redirect to="/de/veneers" />} />
                      <Route path="/composite" element={<Redirect to="/de/composite" />} />
                      <Route path="/technologien" element={<Redirect to="/de/technologien" />} />
                      <Route path="/kontakt" element={<Redirect to="/de/kontakt" />} />
                      <Route path="/datenschutz" element={<Redirect to="/de/datenschutz" />} />
                      <Route path="/impressum" element={<Redirect to="/de/impressum" />} />
                      <Route path="/emergency" element={<Redirect to="/de/notdienst" />} />
                      <Route path="/implants" element={<Redirect to="/en/implants" />} />
                      <Route path="/contact" element={<Redirect to="/en/contact" />} />
                      <Route path="/technologies" element={<Redirect to="/en/technologies" />} />
                      <Route path="/legal-notice" element={<Redirect to="/en/legal-notice" />} />
                      <Route path="/privacy" element={<Redirect to="/en/privacy" />} />
                      <Route path="/book" element={<Navigate to="/de/kontakt?booking=true" replace />} />
                      <Route path="/booking" element={<Navigate to="/de/kontakt?booking=true" replace />} />

                      {/* Unbekannte öffentliche Pfade → Default-Sprache */}
                      <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
                    </Routes>
                  </div>
                  <Footer />
                  <EmergencyButton />
                  <CookieConsent />
                </div>
              </FormProvider>
            </LanguageProvider>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
